import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { Search, Add, Psychology } from '@mui/icons-material';
import { GreeningBrandLabel } from '../components/greening/GreeningBrandLabel';

const EM_BREVE = 'Em breve';

export function RadarClientePage() {
  const theme = useTheme();
  const [rowsPerPage] = useState(10);

  return (
    <>
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Radar por cliente
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Acompanhe o que está sendo controlado no Radar: empresa, tipo/número, status e anexos trazidos pelo BOT.
        </Typography>
        <Alert severity="info" icon={false}>
          O módulo <strong>Radar por cliente</strong> está <strong>{EM_BREVE.toLowerCase()}</strong>. Cadastro de
          processos, execução do BOT, consultas e histórico serão habilitados em uma próxima versão.
        </Alert>
      </Box>

      <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, mb: 2 }}>
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
              px: 1.5,
              py: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 86 }} disabled>
                <Select value={rowsPerPage} displayEmpty>
                  <MenuItem value={10}>10</MenuItem>
                </Select>
              </FormControl>
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#7a7a7a', textTransform: 'uppercase' }}>
                Itens por página
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#7a7a7a', textTransform: 'uppercase' }}>
                Pesquisar
              </Typography>
              <TextField
                size="small"
                placeholder="Pesquisar"
                disabled
                InputProps={{
                  startAdornment: <Search sx={{ fontSize: 18, color: '#909090', mr: 1 }} />,
                }}
                sx={{ width: { xs: '100%', sm: 280 } }}
              />
              <Tooltip title={EM_BREVE}>
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Add />}
                    disabled
                    sx={{ boxShadow: 'none', height: 32, fontWeight: 800, textTransform: 'uppercase' }}
                  >
                    Novo
                  </Button>
                </span>
              </Tooltip>
              <Chip label={EM_BREVE} size="small" variant="outlined" color="primary" />
            </Box>
          </Box>

          <Divider />

          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1100 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.common.black, 0.06) }}>
                  {['Empresa', 'Órgão', 'Tipo', 'Número', 'Status atual', 'Anexo', 'Última consulta'].map((col) => (
                    <TableCell
                      key={col}
                      sx={{ fontSize: 11, fontWeight: 900, color: '#5a5a5a', textTransform: 'uppercase' }}
                    >
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 4 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#808080', textAlign: 'center' }}>
                      {EM_BREVE}: cadastro e execução do BOT para controle de licenças e processos ambientais.
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Box>

          <Divider />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
              px: 1.5,
              py: 1,
            }}
          >
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#7a7a7a', textTransform: 'uppercase' }}>
              Mostrando de 0 até 0 de 0 registros
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button size="small" variant="text" disabled>
                « Anterior
              </Button>
              <Button size="small" variant="contained" disabled sx={{ minWidth: 28, height: 28, px: 0, boxShadow: 'none', fontWeight: 800 }}>
                1
              </Button>
              <Button size="small" variant="text" disabled>
                Próximo »
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#5a5a5a', textTransform: 'uppercase' }}>
              Resultado encontrado
            </Typography>
            <Chip label={EM_BREVE} size="small" variant="outlined" />
          </Box>
          <Divider sx={{ my: 1.25 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#808080' }}>
            {EM_BREVE}: a consulta ao órgão ambiental e o registro do resultado serão exibidos aqui.
          </Typography>
        </CardContent>
      </Card>

      <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Psychology sx={{ fontSize: 20, color: theme.palette.success.main }} />
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#5a5a5a', textTransform: 'uppercase' }}>
              Análise consolidada — <GreeningBrandLabel fontWeight={800} fontSize={12} />
            </Typography>
            <Chip label={EM_BREVE} size="small" variant="outlined" color="success" />
          </Box>
          <Divider sx={{ my: 1.25 }} />
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#404040', mb: 1 }}>
            Agente de Inteligência <GreeningBrandLabel fontWeight={600} fontSize={13} /> (legislação ambiental)
          </Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#808080' }}>
            {EM_BREVE}: visão agregada e anonimizada das dúvidas e padrões de todos os clientes sobre normas
            monitoradas (LGPD). Na modal de cada mudança legislativa, o usuário vê apenas a própria conversa com o
            agente.
          </Typography>
        </CardContent>
      </Card>

      <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#5a5a5a', textTransform: 'uppercase' }}>
              Histórico de consultas
            </Typography>
            <Chip label={EM_BREVE} size="small" variant="outlined" />
          </Box>
          <Divider sx={{ my: 1.25 }} />
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#808080' }}>
            {EM_BREVE}: o histórico de execuções do BOT por empresa será listado aqui.
          </Typography>
        </CardContent>
      </Card>
    </>
  );
}
